import React, { useRef, useState } from 'react';
import './Heatmap.css';

const LOUPE_ZOOM = 2.5;
const LOUPE_SIZE = 96; // как в Heatmap.css
const KEY_STEP = 0.01;

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const round4 = (v) => Math.round(v * 10000) / 10000;

/**
 * Участник отмечает точки на картинке: нажатие ставит точку, её можно перетащить (рядом с пальцем — лупа),
 * стрелками на клавиатуре — сдвинуть. Когда точек максимум, новое нажатие переносит самую старую.
 * value: [{ x, y }] — доли ширины и высоты картинки.
 */
function HeatmapPicker({ image, value = [], max = 1, onChange }) {
    const imgRef = useRef(null);
    const dragRef = useRef(null);
    const [loupe, setLoupe] = useState(null);
    const src = image.startsWith('http') || image.startsWith('/static') ? image : `/proxy/image/${image}`;

    // Считаем по видимой картинке, а не по рамке вокруг неё
    const toPoint = (e) => {
        const rect = imgRef.current.getBoundingClientRect();
        return { x: round4(clamp01((e.clientX - rect.left) / rect.width)), y: round4(clamp01((e.clientY - rect.top) / rect.height)) };
    };

    const place = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        const point = toPoint(e);
        const next = value.length >= max ? [...value.slice(1), point] : [...value, point];
        onChange(next);
        startDrag(e, next.length - 1);
    };

    const startDrag = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = { index, pointerId: e.pointerId };
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setLoupe({ ...toPoint(e), index });
    };

    const move = (e) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        const point = toPoint(e);
        setLoupe({ ...point, index: drag.index });
        onChange(value.map((p, i) => (i === drag.index ? point : p)));
    };

    const end = (e) => {
        if (!dragRef.current || dragRef.current.pointerId !== e.pointerId) return;
        dragRef.current = null;
        setLoupe(null);
    };

    const nudge = (index, e) => {
        const shift = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
        if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            onChange(value.filter((_, i) => i !== index));
            return;
        }
        if (!shift) return;
        e.preventDefault();
        const step = e.shiftKey ? KEY_STEP * 5 : KEY_STEP;
        onChange(value.map((p, i) => (i === index
            ? { x: round4(clamp01(p.x + shift[0] * step)), y: round4(clamp01(p.y + shift[1] * step)) }
            : p)));
    };

    // Без мыши и пальца: Enter или пробел на картинке ставят точку в центр, дальше — стрелками
    const addByKeyboard = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const point = { x: 0.5, y: 0.5 };
        onChange(value.length >= max ? [...value.slice(1), point] : [...value, point]);
    };

    const left = Math.max(0, max - value.length);
    return (
        <div className="heatmap-picker">
            <div
                className="heatmap-picker__frame"
                onPointerDown={place}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
                onKeyDown={addByKeyboard}
                role="application"
                tabIndex={0}
                aria-label={`Картинка: нажмите, чтобы поставить точку. Можно поставить ${max === 1 ? 'одну точку' : `до ${max} точек`}.`}
            >
                <img ref={imgRef} src={src} alt="Картинка вопроса" draggable={false} />
                {value.map((p, i) => (
                    <button
                        key={i}
                        type="button"
                        className={`heatmap-picker__pin${loupe?.index === i ? ' is-dragging' : ''}`}
                        style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                        onPointerDown={(e) => startDrag(e, i)}
                        onKeyDown={(e) => nudge(i, e)}
                        aria-label={`Точка ${i + 1}: ${Math.round(p.x * 100)}% слева, ${Math.round(p.y * 100)}% сверху. Стрелки — сдвинуть, Delete — убрать.`}
                    >
                        {max > 1 ? i + 1 : ''}
                    </button>
                ))}
                {loupe && imgRef.current && (() => {
                    // Лупа показывает увеличенный участок вокруг точки: её центр — ровно под пальцем
                    const bw = imgRef.current.clientWidth * LOUPE_ZOOM;
                    const bh = imgRef.current.clientHeight * LOUPE_ZOOM;
                    return (
                    <span
                        className="heatmap-picker__loupe"
                        aria-hidden
                        style={{
                            left: `${loupe.x * 100}%`,
                            top: `${loupe.y * 100}%`,
                            backgroundImage: `url("${src}")`,
                            backgroundSize: `${bw}px ${bh}px`,
                            backgroundPosition: `${LOUPE_SIZE / 2 - loupe.x * bw}px ${LOUPE_SIZE / 2 - loupe.y * bh}px`,
                        }}
                    />
                    );
                })()}
            </div>
            <div className="heatmap-picker__bar">
                <span>
                    {value.length === 0
                        ? (max === 1 ? 'Нажмите на картинку, чтобы отметить место' : `Нажмите на картинку — можно отметить до ${max} мест`)
                        : left > 0
                            ? `Точек: ${value.length} из ${max}. Можно добавить ещё или перетащить точку`
                            : (max === 1 ? 'Точка отмечена — её можно перетащить' : `Отмечено ${max} из ${max}: новое нажатие перенесёт самую раннюю точку`)}
                </span>
                {value.length > 0 && (
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => onChange([])}>
                        Убрать {value.length > 1 ? 'точки' : 'точку'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default HeatmapPicker;
